import * as React from "react";
import { Formik } from "formik";
import * as _ from "lodash";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";

import { FilterParams } from "src/library/utils";
import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import {
  Input,
  Select
} from "src/library/components/Forms";
import { CascadeSettingFieldLabel } from "src/library/components/Forms/CascadeSettingField";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import { Switch } from "src/library/components/Forms/Switch";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import DeliveryDaysPicker from "src/core/shared/components/DeliveryDaysPicker";
import { ERPRJL } from "src/utils/constants";
import { deliveryRoutesApi } from "src/core/shared/api/deliveryRoutes.api";
import {
  UpdateSettingParams,
  useInheritedSettingsApi
} from "src/core/shared/api/inheritedSettings.api";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  IInheritedSetting,
  InheritedSettingValue,
  OrderedInheritedSettings,
  IDeliveryRoute,
  DataRequirement
} from "src/core/drops/api/drops.api";
import {
  InheritedRadioRow,
  InheritedSettingsWrapper,
  InheritedSettingsField
} from "src/core/shared/components/Inherited-settings";
import { useCurrentUser } from "src/core/users/hooks";
import { OrderDataRequirements } from "src/core/shared/components/Inherited-settings/OrderDataRequirements";
import { TimePicker } from "src/core/shared/components/Date/TimePicker";

import MinimumOrderQuantitySetting from "./MinimumOrderQuantity/MinimumOrderQuantitySetting";

type IGlobalSettingFormFields = { [P in IFormFields]: IFormValue<IFormFields> };
type IFormFields = keyof Omit<InheritedSettingValue, "delivery_week_leadtimes" | "delivery_cutoff" | "point_of_contact" | "credit_request_auto_approval">;

export type SetUpdatingSettings = { [P in IFormFields]?: "not-changed" | "required" | "loading" };

export interface IFormValue<T> {
  name: T;
  inherited: boolean;
  value?: any;
  Description?: JSX.Element;
  SettingElement: (setFieldValue: any, setting: IFormValue<T>, handleBlur?: any) => React.ReactNode;
  hidden?: boolean;
}

/**
 * Main settings page
 *
 * @returns
 */
const OrderSettingsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology } = props;

  const { erp, installedModules } = useMappedState(({ global }: IStore) => ({
    erp: global.settings && global.settings.erp.name,
    installedModules: global.settings?.installedModules || []
  }));

  const checkPermission = useCheckUserPermission();
  const canAmend = checkPermission("amend-global-settings");
  const [ getSettingsResponse, getSettings ] = useInheritedSettingsApi("GET_GLOBAL_SETTINGS");
  const [ updateSettingResponse, updateSetting ] = useInheritedSettingsApi("UPDATE_SETTING");

  const [ updatingSettings, setUpdatingSettings ] = React.useState<SetUpdatingSettings>({
    minimum_order_value: "not-changed",
    minimum_order_quantity: "not-changed",
    delivery_route: "not-changed"
  });

  const [ { currentRole } ] = useCurrentUser();
  const formRef = React.useRef<Formik<IGlobalSettingFormFields>>(null);
  const [ globalSettings, setGlobalSettings ] = React.useState<OrderedInheritedSettings<IInheritedSetting>>();
  const [ getDeliveryDaysResponse, getDeliveryDays ] = deliveryRoutesApi<IDeliveryRoute[]>("GET_DELIVERY_ROUTES");
  const [ updateDeliveryRouteResponse, updateDeliveryRoute ] = deliveryRoutesApi<IDeliveryRoute>("UPDATE_DELIVERY_ROUTE");
  const dispatch = useDispatch();
  const isLoading = React.useMemo(() => updateSettingResponse.pending || updateDeliveryRouteResponse.pending, [ updateDeliveryRouteResponse.pending, updateSettingResponse.pending ]);

  /**
   * Sort delivery days
   */
  const sortDeliveryDays = React.useCallback((days: IDeliveryRoute[]) => {
    const { deliveryDays } = days[ 0 ];
    const test = _.mapValues(_.invert(_.invert(deliveryDays)), parseInt);

    days[ 0 ].deliveryDays = test;

    return days;
  }, []);

  /**
   * Compile the settings
   */
  const settings: IGlobalSettingFormFields | null = React.useMemo(() => {
    // Return null if there are no settings found
    if (!(globalSettings && getDeliveryDaysResponse.data)) {
      return null;
    }

    return {
      order_mode: {
        name: "order_mode",
        inherited: true,
        value: globalSettings && globalSettings.order_mode ? globalSettings.order_mode.value : { allow: "DL" },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`${terminology.order.singular.text} Mode`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`Select how your ${terminology.customer.plural.text} can make ${
                terminology.order.plural.text
              }. Made to order products will not be visible on date-led orders.`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur) => (
          <Select
            value={setting.value.allow}
            className="select-box"
            name={setting.name}
            disabled={!canAmend}
            autoHideLabels
            label={`Global ${terminology.cutOff.singular.text}`}
            options={[
              {
                value: "DL",
                text: "Date led ordering only",
                checked: setting.value.allow === "DL"
              },
              {
                value: "MTO",
                text: "Made to order only",
                checked: setting.value.allow === "MTO"
              },
              {
                value: "ANY",
                text: "Both made to order and date led ordering",
                checked: setting.value.allow === "ANY"
              }
            ]}
            handleChange={selectProps => {
              setFieldValue(setting.name, {
                ...setting,
                value: { allow: selectProps.currentTarget.value }
              });
              setUpdatingSettings({ order_mode: "required" });
            }}
            handleBlur={handleBlur}
          />
        )
      },
      minimum_order_quantity: {
        name: "minimum_order_quantity",
        inherited: true,
        value:
              globalSettings && globalSettings.minimum_order_quantity ?
                globalSettings && globalSettings.minimum_order_quantity.value :
                { quantity: 0 },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`Minimum ${terminology.order.singular.text} quantity`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This adds validation on ${
                terminology.order.plural.text
              } to stop low quantity ${
                terminology.order.plural.text
              } being placed`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur) => (
          <MinimumOrderQuantitySetting
            setting={setting as IFormValue<"minimum_order_quantity">}
            canAmend={canAmend}
            setFieldValue={setFieldValue}
            setUpdatingSettings={setUpdatingSettings}
            handleBlur={handleBlur}
          />
        )
      },
      minimum_order_value: {
        name: "minimum_order_value",
        inherited: true,
        value: globalSettings && globalSettings.minimum_order_value ? globalSettings && globalSettings.minimum_order_value.value : { value: 0 },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`Minimum ${terminology.order.singular.text} value`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This adds validation on ${
                terminology.order.plural.text
              } to stop low value ${
                terminology.order.plural.text
              } being placed`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, handleBlur) => (
          <Input
            value={setting.value.value}
            className="input"
            inputType="number"
            name={setting.name}
            disabled={!canAmend || (updatingSettings && updatingSettings[ setting.name ] === "loading")}
            autoHideLabels
            label={`Minimum ${terminology.order.singular.text} value`}
            handleChange={inputProps => {
              setFieldValue(setting.name, {
                ...setting,
                value: { value: inputProps.currentTarget.value }
              });
              setUpdatingSettings({ minimum_order_value: "required" });
            }}
            handleBlur={handleBlur}
          />
        )
      },
      one_daily_delivery: {
        name: "one_daily_delivery",
        inherited: true,
        value: globalSettings && globalSettings.one_daily_delivery ? globalSettings && globalSettings.one_daily_delivery.value : { value: 0 },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`One ${terminology.order.singular.text} per day`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This ensures that a maximum of one ${terminology.order.singular.text} is placed per day`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => (
          <InheritedRadioRow direction="horizontal">
            <Switch
              disabled={!canAmend}
              onChange={checked => {
                setFieldValue(setting.name, {
                  ...setting,
                  value: { value: checked }
                });
                setUpdatingSettings({ one_daily_delivery: "required" });
              }}
              checked={setting.value.value}
            />

            <CascadeSettingFieldLabel
              for={`${setting.name}-default`}
              label="Only one order per day"
            />
          </InheritedRadioRow>
        )
      },
      order_data_requirements: {
        name: "order_data_requirements",
        inherited: true,
        value:
              globalSettings && globalSettings.order_data_requirements ?
                globalSettings && globalSettings.order_data_requirements.value :
                { orderPONumber: false },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Required PO number
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This adds validation on ${terminology.order.plural.text} to prevent ${terminology.order.plural.text} being placed without a PO number`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => {
          const settingValue: DataRequirement = (setting as IFormValue<"order_data_requirements">).value.orderPONumber;

          return (
            <>
              <InheritedRadioRow direction="horizontal">
                <Switch
                  disabled={!canAmend}
                  onChange={checked => {
                    setFieldValue(setting.name, {
                      ...setting,
                      value: {
                        orderPONumber: {
                          ...(typeof settingValue === "object" ? settingValue : {}),
                          required: checked
                        }
                      }
                    });
                    setUpdatingSettings({ order_data_requirements: "required" });
                  }}
                  checked={typeof settingValue === "object" ? settingValue.required : setting.value.orderPONumber}
                />

                <CascadeSettingFieldLabel
                  for={`${setting.name}-default`}
                  label="Require a PO number"
                />
              </InheritedRadioRow>

              {typeof settingValue === "object" && settingValue.required && (
                <OrderDataRequirements
                  settingValue={setting.value}
                  terminology={terminology}
                  setSettingValue={value => {
                    setFieldValue(setting.name, {
                      ...setting,
                      value
                    });
                    setUpdatingSettings({ order_data_requirements: "required" });
                  }}
                />
              )}
            </>
          );
        }
      },
      price_visible: {
        name: "price_visible",
        inherited: true,
        value: globalSettings && globalSettings.price_visible ? globalSettings && globalSettings.price_visible.value : { visible: true },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Display prices
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets the visibility of prices on the platform
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => (
          <InheritedRadioRow direction="horizontal">
            <Switch
              disabled={!canAmend}
              onChange={checked => {
                setFieldValue(setting.name, {
                  ...setting,
                  value: { visible: checked }
                });
                setUpdatingSettings({ price_visible: "required" });
              }}
              checked={setting.value.visible}
            />

            <CascadeSettingFieldLabel
              for={`${setting.name}-default`}
              label={`Make prices visible to ${terminology.customer.plural.text}`}
            />
          </InheritedRadioRow>
        )
      },
      require_payment_method: {
        name: "require_payment_method",
        inherited: true,
        value: globalSettings && globalSettings.require_payment_method ? globalSettings && globalSettings.require_payment_method.value : { required: false },
        hidden: !installedModules.includes("payment"),
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Require payment method
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This sets whether a payment method is required by the ${terminology.customer.singular.text} to create an order.`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => (
          <InheritedRadioRow direction="horizontal">
            <Switch
              disabled={!canAmend}
              onChange={checked => {
                setFieldValue(setting.name, {
                  ...setting,
                  value: { required: checked }
                });
                setUpdatingSettings({ require_payment_method: "required" });
              }}
              checked={setting.value.required}
            />

            <CascadeSettingFieldLabel
              for={`${setting.name}-default`}
              label={`A payment method is required by the ${terminology.customer.singular.text}`}
            />
          </InheritedRadioRow>
        )
      },
      allow_waste_recording: {
        name: "allow_waste_recording",
        inherited: true,
        value: globalSettings && globalSettings.allow_waste_recording ? globalSettings && globalSettings.allow_waste_recording.value : { allowed: false },
        hidden: !installedModules.includes("waste"),
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow waste recording
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets whether waste recording is is allowed by default
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => (
          <InheritedRadioRow direction="horizontal">
            <Switch
              disabled={!canAmend}
              onChange={checked => {
                setFieldValue(setting.name, {
                  ...setting,
                  value: { allowed: checked }
                });
                setUpdatingSettings({ allow_waste_recording: "required" });
              }}
              checked={setting.value.allowed}
            />

            <CascadeSettingFieldLabel
              for={`${setting.name}-default`}
              label={`${terminology.customer.plural.text} allow waste recording`}
            />
          </InheritedRadioRow>
        )
      },
      allow_substitutions: {
        name: "allow_substitutions",
        inherited: true,
        value: globalSettings && globalSettings.allow_substitutions ? globalSettings && globalSettings.allow_substitutions.value : { allowed: false },
        hidden: !installedModules.includes("substitution"),
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow substitutions
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets whether substitutions are allowed by default
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => (
          <InheritedRadioRow direction="horizontal">
            <Switch
              disabled={!canAmend}
              onChange={checked => {
                setFieldValue(setting.name, {
                  ...setting,
                  value: { allowed: checked }
                });
                setUpdatingSettings({ allow_substitutions: "required" });
              }}
              checked={setting.value.allowed}
            />

            <CascadeSettingFieldLabel
              for={`${setting.name}-default`}
              label={`${terminology.customer.plural.text} allow substitutions`}
            />
          </InheritedRadioRow>
        )
      },
      allow_holidays: {
        name: "allow_holidays",
        inherited: true,
        value: globalSettings && globalSettings.allow_holidays ? globalSettings && globalSettings.allow_holidays.value : { allowed: false },
        hidden: !installedModules.includes("shutdown"),
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow holidays
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets whether customers can setup their own holiday schedules to block orders for certain date ranges
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => (
          <InheritedRadioRow direction="horizontal">
            <Switch
              disabled={!canAmend}
              onChange={checked => {
                setFieldValue(setting.name, {
                  ...setting,
                  value: { allowed: checked }
                });
                setUpdatingSettings({ allow_holidays: "required" });
              }}
              checked={setting.value.allowed}
            />

            <CascadeSettingFieldLabel
              for={`${setting.name}-default`}
              label={`${terminology.customer.plural.text} allow holidays`}
            />
          </InheritedRadioRow>
        )
      },
      allow_order_customer_comments: {
        name: "allow_order_customer_comments",
        inherited: true,
        value: globalSettings && globalSettings.allow_order_customer_comments ? globalSettings && globalSettings.allow_order_customer_comments.value : { allowed: false },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow Order Comments
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets whether customers can leave comments on an order and which admin user groups are notified
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => {
          return (
            <>
              <InheritedRadioRow direction="horizontal">
                <Switch
                  disabled={!canAmend}
                  onChange={value => {
                    setFieldValue(setting.name, {
                      ...setting,
                      value: {
                        ...setting.value,
                        allowed: value
                      }
                    });
                    setUpdatingSettings({ allow_order_customer_comments: "required" });
                  }}
                  checked={setting.value.allowed}
                />

                <CascadeSettingFieldLabel
                  for={`${setting.name}-default`}
                  label={`${terminology.customer.plural.text} allow order comments`}
                />

              </InheritedRadioRow>
            </>
          );
        }
      },
      allow_delivery_time: {
        name: "allow_delivery_time",
        hidden: !installedModules.includes("odoo"),
        inherited: true,
        value: globalSettings && globalSettings.allow_delivery_time ? globalSettings && globalSettings.allow_delivery_time.value : { allowed: false },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Allow Requested Delivery Time
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              This sets whether customers can request a specific delivery time
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => {
          return (
            <>
              <InheritedRadioRow direction="horizontal">
                <Switch
                  disabled={!canAmend}
                  onChange={value => {
                    setFieldValue(setting.name, {
                      ...setting,
                      value: {
                        ...setting.value,
                        allowed: value
                      }
                    });
                    setUpdatingSettings({ allow_delivery_time: "required" });
                  }}
                  checked={setting.value.allowed}
                />

                <CascadeSettingFieldLabel
                  for={`${setting.name}-default`}
                  label={`${terminology.customer.plural.text} allow requested delivery time`}
                />
              </InheritedRadioRow>

              {typeof setting.value === "object" && setting.value.allowed && (

                <TimePicker
                  value={setting.value.defaultTime}
                  name="Default Time"
                  timezone="GMT"
                  label="Default Time"
                  forceSelect
                  setTime={value => {
                    setFieldValue(setting.name, {
                      ...setting,
                      value: {
                        ...setting.value,
                        defaultTime: value ?? null
                      }

                    });
                    setUpdatingSettings({ allow_delivery_time: "required" });
                  }}
                />

              )}

            </>
          );
        }
      },
      delivery_route: {
        name: "delivery_route",
        inherited: true,
        value: globalSettings && globalSettings.delivery_route ? globalSettings.delivery_route.deliveryRoute : null,
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`Allowed ${terminology.delivery.singular.text} days`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`Controls which days ${terminology.delivery.plural.text} can be created for`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting) => {
          if (getDeliveryDaysResponse.data && globalSettings && globalSettings.delivery_route && globalSettings.delivery_route.deliveryRoute) {
            return (
              <DeliveryDaysPicker
                disabled={!canAmend}
                deliveryRoutes={sortDeliveryDays(getDeliveryDaysResponse.data)}
                value={setting.value}
                inherited={null}
                isGlobal
                type="global"
                handleChange={deliveryRoute => {
                  if (deliveryRoute) {
                    deliveryRoute.entityId = null;

                    setFieldValue(setting.name, {
                      ...setting,
                      value: deliveryRoute
                    });
                    setUpdatingSettings({ delivery_route: "required" });
                  }
                }}
              />
            );
          }

          return null;
        }
      }
    };
  }, [
    canAmend,
    getDeliveryDaysResponse.data,
    globalSettings,
    installedModules,
    sortDeliveryDays,
    terminology.customer.plural.text,
    terminology.customer.singular.text,
    terminology.cutOff.singular.text,
    terminology.delivery.plural.text,
    terminology.delivery.singular.text,
    terminology.order.plural.text,
    terminology.order.singular.text,
    updatingSettings,
    currentRole
  ]);

  const transformValuePreSubmit = React.useCallback((settingName: IFormFields, value: InheritedSettingValue) => {
    if (settingName === "minimum_order_quantity") {
      // Turn all the property values into numbers
      return _.mapValues(value, (val, _k) => typeof val === "string" ? parseFloat(val) : val);
    }

    return value;
  }, []);

  /**
   * Handle submit settings
   */
  const handleSubmitSettings = React.useCallback((form: IGlobalSettingFormFields) => {
    _.each(form, setting => {
      if (updatingSettings[ setting.name ] === "required") {
        if (setting.name === "delivery_route") {
          updateDeliveryRoute(setting.value);
        } else {
          if (globalSettings) {
            updateSetting({
              ...globalSettings[ setting.name ],
              value: transformValuePreSubmit(setting.name, setting.value)
            } as UpdateSettingParams);
          }
        }
      }
    });
  }, [
    globalSettings,
    updateDeliveryRoute,
    updateSetting,
    updatingSettings,
    transformValuePreSubmit
  ]);

  // On mount
  React.useEffect(() => {
    getSettings({});
    getDeliveryDays<FilterParams<IDeliveryRoute>>({ type: "global" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ getSettings ]);

  // On getSettingsResponse change
  React.useEffect(() => {
    if (getSettingsResponse.data) {
      const settings: OrderedInheritedSettings<IInheritedSetting> = {};

      _.each(getSettingsResponse.data, setting => {
        if (setting) {
          settings[ setting.name ] = setting;
        }
      });
      setGlobalSettings(settings);
    }

    if (getSettingsResponse.error) {
      dispatch(setGlobalError(getSettingsResponse.error));
    }
  }, [ dispatch, getSettingsResponse ]);

  // On updateSettingResponse change
  React.useEffect(() => {
    if (updateSettingResponse.data) {
      setUpdatingSettings({ [ updateSettingResponse.data.name ]: false });
      toast.success("Settings were correctly saved.");
    }

    if (updateSettingResponse.error) {
      toast.error("Could not save settings.");
    }
  }, [ updateSettingResponse ]);

  // On updateDeliveryRouteResponse change
  React.useEffect(() => {
    if (updateDeliveryRouteResponse.data) {
      setUpdatingSettings({ delivery_route: "not-changed" });
      toast.success("Settings were correctly saved.");
    }

    if (updateDeliveryRouteResponse.error) {
      toast.error("Could not save settings.");
    }
  }, [ updateDeliveryRouteResponse ]);

  if (erp === ERPRJL && settings && settings.price_visible) {
    // @ts-ignore
    delete settings.price_visible;
  }

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Grid.Bounds direction="vertical">
          {!settings ? (
            <LoadingSpinner
              type="circular"
              offsetTop={50}
            />
          ) : (
            <Formik
              ref={formRef}
              initialValues={settings}
              onSubmit={handleSubmitSettings}
              render={({
                handleSubmit, handleBlur, values, setFieldValue
              }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    {_.map(values, (setting, i) => {
                      if (setting.hidden) {
                        return null;
                      }

                      return (
                        <InheritedSettingsWrapper key={`setting-${i}`}>
                          <InheritedSettingsField
                            disabled={updatingSettings && updatingSettings[ setting.name ] === "loading"}
                            id={`${setting.name}-${i}`}
                            key={setting.name}
                          >
                            {setting.Description}

                            {setting.SettingElement(setFieldValue, setting, handleBlur)}
                          </InheritedSettingsField>
                        </InheritedSettingsWrapper>
                      );
                    })}

                    {canAmend && (
                      <Button
                        loading={isLoading}
                        disabled={isLoading}
                        type="submit"
                      >
                        Save
                      </Button>
                    )}
                  </form>
                );
              }}
            />
          )}
        </Grid.Bounds>
      </MainContentWrapper>
    </Layout>
  );
};

export default OrderSettingsPage;
